// Chart of Account Types
export interface ChartOfAccount {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  parentAccountId?: number;
  parentAccountName?: string;
  parentAccountCode?: string;
  accountType: AccountType;
  accountNature: AccountNature;
  level: number;
  isMainAccount: boolean;
  isFinalAccount: boolean;
  isActive: boolean;
  currencyCode: string;
  allowManualEntry: boolean;
  requireCostCenter: boolean;
  openingBalance: number;
  currentBalance: number;
  description?: string;
  descriptionAr?: string;
  notes?: string;
  createdDate: string;
  createdByName?: string;
  modifiedDate?: string;
  modifiedByName?: string;
  childAccounts?: ChartOfAccount[];
  hasChildren: boolean;
  childrenCount: number;
}

export interface CreateChartOfAccountDto {
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  parentAccountId?: number;
  accountType: AccountType;
  accountNature: AccountNature;
  level?: number;
  isMainAccount?: boolean;
  isFinalAccount?: boolean;
  isActive?: boolean;
  currencyCode?: string;
  allowManualEntry?: boolean;
  requireCostCenter?: boolean;
  openingBalance?: number;
  description?: string;
  descriptionAr?: string;
  notes?: string;
}

export interface UpdateChartOfAccountDto {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  parentAccountId?: number;
  accountType: AccountType;
  accountNature: AccountNature;
  isActive: boolean;
  isMainAccount: boolean;
  isFinalAccount: boolean;
  currencyCode: string;
  allowManualEntry: boolean;
  requireCostCenter: boolean;
  openingBalance: number;
  description?: string;
  descriptionAr?: string;
  notes?: string;
}

export type AccountType = 'Asset' | 'Liability' | 'Equity' | 'Revenue' | 'Expense';
export type AccountNature = 'Debit' | 'Credit';

export interface ChartOfAccountFilters {
  includeInactive?: boolean;
  accountType?: AccountType;
  onlyFinalAccounts?: boolean;
}

// Account Type Options for UI
export const ACCOUNT_TYPES: { value: AccountType; labelEn: string; labelAr: string }[] = [
  { value: 'Asset', labelEn: 'Assets', labelAr: 'الأصول' },
  { value: 'Liability', labelEn: 'Liabilities', labelAr: 'الخصوم' },
  { value: 'Equity', labelEn: 'Equity', labelAr: 'حقوق الملكية' },
  { value: 'Revenue', labelEn: 'Revenue', labelAr: 'الإيرادات' },
  { value: 'Expense', labelEn: 'Expenses', labelAr: 'المصروفات' },
];

// Account Nature Options
export const ACCOUNT_NATURES: { value: AccountNature; labelEn: string; labelAr: string }[] = [
  { value: 'Debit', labelEn: 'Debit', labelAr: 'مدين' },
  { value: 'Credit', labelEn: 'Credit', labelAr: 'دائن' },
];

// Helper function to get account nature based on type
export const getAccountNatureByType = (accountType: AccountType): AccountNature => {
  switch (accountType) {
    case 'Asset':
    case 'Expense':
      return 'Debit';
    case 'Liability':
    case 'Equity':
    case 'Revenue':
      return 'Credit';
    default:
      return 'Debit';
  }
};

// Helper function to get account type label
export const getAccountTypeLabel = (type: AccountType, lang: 'en' | 'ar' = 'ar'): string => {
  const accountType = ACCOUNT_TYPES.find(t => t.value === type);
  return lang === 'ar' ? accountType?.labelAr || type : accountType?.labelEn || type;
};

// Helper function to get account nature label
export const getAccountNatureLabel = (nature: AccountNature, lang: 'en' | 'ar' = 'ar'): string => {
  const accountNature = ACCOUNT_NATURES.find(n => n.value === nature);
  return lang === 'ar' ? accountNature?.labelAr || nature : accountNature?.labelEn || nature;
};